import ADrawer from '@/components/A/ADrawer/ADrawer'
import styles from './InfoDrawerBtn.module.less'
import { CSSProperties, useState } from 'react'
import AIcon from '@/components/A/AIcon/AIcon'
import { useTranslation } from 'react-i18next'
import { commonUtils } from '@/helpers/commonUtils'
import { GENDER_MAPS, INDUSTRY_CATEGORY_MAPS, TARGET_MARKTS_MAPS } from '@/i18nEnumConsts'
import MiddleText from '@/components/MiddleText/MiddleText'
import AInput from '@/components/A/AInput/AInput'
import AButton from '@/components/A/AButton/AButton'
import { api } from '@/api'
import { isPortraitByProjectInfo } from '@/helpers/productCategory'
import { getIndustryDisplayName } from '@/helpers/industry'

interface Props {
  mode: 'default' | 'professor'
  project: ProjectModels.ProjectDetail | ProjectModels.ProjectDetailV3
  onProjectUpdate?: () => void
}

function InfoDrawerBtn(props: Props) {
  const { t, i18n } = useTranslation()
  const { project } = props
  const isPortraitMode = isPortraitByProjectInfo(project)
  const durationValue = Number(project.adDuration)
  const inferredSecondCount = Number.isFinite(durationValue) && durationValue > 0
    ? Math.ceil(durationValue)
    : Math.max(project.detailMetrics?.length || 0, project.frameInfos?.length || 0)
  const resolvedDurationCategory: ProjectModels.DurationCategory =
    project.durationCategory ||
    (inferredSecondCount > 0 && inferredSecondCount <= 10 ? 'five_to_ten' : 'ten_plus')
  const creativeTypeText = resolvedDurationCategory === 'five_to_ten'
    ? t('dashboard.projectCreatePage.durationCategoryOptions.fiveToTen')
    : t('dashboard.projectCreatePage.durationCategoryOptions.tenPlus')
  const lng = i18n.language as keyof typeof INDUSTRY_CATEGORY_MAPS
  const industryIngMap = INDUSTRY_CATEGORY_MAPS[lng] as Record<string, string>
  const brandIndustry = getIndustryDisplayName(props.project.brandIndustry, industryIngMap)
  const genderMap = GENDER_MAPS[lng] as Record<string, string>
  const gender = genderMap[props.project.adCustomerGender] || props.project.adCustomerGender
  const targetMarketMap = TARGET_MARKTS_MAPS[lng] as Record<string, string>
  const targetMarket = targetMarketMap[props.project.adTargetMarket] || props.project.adTargetMarket
  const [openInfoDrawer, setOpenInfoDrawer] = useState<boolean>(false)
  const [drawerMode, setDrawerMode] = useState<'normal' | 'modify'>('normal')
  const [modifyProjectName, setModifyProjectName] = useState<string>(project.projectName)
  const [modifyBrandName, setModifyBrandName] = useState<string>(project.brandName)
  const [modifyAdName, setModifyAdName] = useState<string>(project.adName)

  const handleSave = async () => {
    await api.project.projectUpdate({
      projectId: project.id,
      projectName: modifyProjectName,
      brandName: modifyBrandName,
      adName: modifyAdName,
    }).send()
    setDrawerMode('normal')
    props.onProjectUpdate?.()
  }

  return (
    <>
      <span className={styles.basicInformation} onClick={() => setOpenInfoDrawer(true)} data-testid='project-detail-info-drawer-open-btn'>
        <MiddleText text={t('dashboard.projectDetailPage.basicInformation')} />
        <AIcon type='icon-line-global_arrow1' width={24} height={24} />
      </span>
      {openInfoDrawer && (
        <ADrawer
          testId='project-detail-info-drawer'
          className={styles.infoDrawerWrapper}
          width={500}
          title={
            <div className={styles.titleBox}>
              <MiddleText
                text={t('dashboard.projectDetailPage.basicInformation')}
              />
              {
                /** 专家页是不会出现编辑入口 */
                props.mode === 'default' && drawerMode === 'normal' && (
                  <span className={styles.modifyBtn} onClick={() => setDrawerMode('modify')} data-testid='project-detail-info-drawer-modify-btn'>
                    <AIcon className={styles.modifyIcon} type="icon-line-global_add1" width={16} height={16} />
                    <MiddleText
                      text={t('dashboard.projectDetailPage.infoDrawer.modify')}
                      className={styles.modifyText}
                    />
                  </span>
                )}
            </div>
          }
          open={openInfoDrawer}
          onClose={() => {
            setOpenInfoDrawer(false)
            setDrawerMode('normal')
          }}
        >
          <div className={styles.infoDrawer}>
            <Title text={t('dashboard.projectDetailPage.basicInformation')} />
            <div className={styles.informationBox}>
              <img
                className={styles.boxBg}
                src={commonUtils.getImagePath('projectDetailPage/range-bg.png')}
                alt=''
              />
              <div className={styles.informationBoxInner}>
                <div className={styles.informationItem}>
                  <span className={styles.label}>
                    {t('dashboard.projectDetailPage.infoDrawer.projectName')}:{' '}
                  </span>
                  {
                    drawerMode === 'normal'
                      ? <span className={styles.value}>{project.projectName}</span>
                      : <AInput testId='project-detail-info-project-name-input' className={styles.modifyInput}
                        defaultValue={project.projectName} showCount maxLength={40}
                        onKeyDown={(e) => {
                          e.stopPropagation()
                        }}
                        onChange={(e) => setModifyProjectName(e.target.value)} />
                  }
                </div>
                <div className={styles.informationItem}>
                  <span className={styles.label}>
                    {t('dashboard.projectDetailPage.infoDrawer.brandName')}:{' '}
                  </span>
                  {
                    drawerMode === 'normal'
                      ? <span className={styles.value}>{project.brandName}</span>
                      : <AInput testId='project-detail-info-brand-name-input' className={styles.modifyInput}
                        defaultValue={project.brandName} showCount maxLength={40}
                        onKeyDown={(e) => {
                          e.stopPropagation()
                        }}
                        onChange={(e) => setModifyBrandName(e.target.value)} />
                  }
                </div>

                <div className={styles.informationItem}>
                  <span className={styles.label}>
                    {t('dashboard.projectDetailPage.infoDrawer.category')}:{' '}
                  </span>
                  <span className={styles.value}>{brandIndustry}</span>
                </div>
                {isPortraitMode && (
                  <div className={styles.informationItem}>
                    <span className={styles.label}>
                      {t('dashboard.projectCreatePage.durationCategory')}:{' '}
                    </span>
                    <span className={styles.value}>{creativeTypeText}</span>
                  </div>
                )}
                <div className={styles.informationItem}>
                  <span className={styles.label}>
                    {t('dashboard.projectDetailPage.infoDrawer.normLabel')}:{' '}
                  </span>
                  <span className={styles.value}>
                    {project.norm === 1
                      ? t('dashboard.projectDetailPage.infoDrawer.norm')
                      : t('dashboard.projectDetailPage.infoDrawer.brandNorm')}
                  </span>
                </div>
              </div>
            </div>
            <Title
              text={t('dashboard.projectDetailPage.infoDrawer.advertisingInformation')}
              style={{ marginTop: 24 }}
            />
            <div className={styles.informationBox}>
              <img
                className={styles.boxBg}
                src={commonUtils.getImagePath('projectDetailPage/range-bg.png')}
                alt=''
              />
              <div className={styles.informationBoxInner}>
                <div className={styles.informationItem}>
                  <span className={styles.label}>
                    {t('dashboard.projectDetailPage.infoDrawer.adName')}:
                  </span>
                  {
                    drawerMode === 'normal'
                      ? <span className={styles.value}>{project.adName}</span>
                      : <AInput testId='project-detail-info-ad-name-input' className={styles.modifyInput}
                        defaultValue={project.adName} showCount maxLength={40}
                        onKeyDown={(e) => {
                          e.stopPropagation()
                        }}
                        onChange={(e) => setModifyAdName(e.target.value)} />
                  }
                </div>
                <div className={styles.informationItem}>
                  <span className={styles.label}>
                    {t('dashboard.projectDetailPage.infoDrawer.targetMarket')}:
                  </span>
                  <span className={styles.value}>{targetMarket}</span>
                </div>
                <div className={styles.informationItem}>
                  <span className={styles.label}>
                    {t('dashboard.projectDetailPage.infoDrawer.taGender')}:
                  </span>
                  <span className={styles.value}>{gender}</span>
                </div>
                <div className={styles.informationItem}>
                  <span className={styles.label}>
                    {t('dashboard.projectDetailPage.infoDrawer.taAge')}:
                  </span>
                  <span className={styles.value}>
                    {t('dashboard.projectDetailPage.infoDrawer.ageRange', {
                      start: project.adCustomerAgeStart,
                      end: project.adCustomerAgeEnd,
                    })}
                  </span>
                </div>
              </div>
            </div>
            {
              drawerMode === 'modify' && <>
                <div className={styles.buttonBox}>
                  <AButton testId='project-detail-info-cancel-btn' size='SM' type='outlined' onClick={() => setDrawerMode('normal')}>
                    <span>{t('dashboard.projectDetailPage.infoDrawer.cancel')}</span>
                  </AButton>
                  <AButton testId='project-detail-info-save-btn' size='SM' type='primary' onClick={() => handleSave()}>
                    <span>{t('dashboard.projectDetailPage.infoDrawer.confirm')}</span>
                  </AButton>
                </div>
              </>
            }
          </div>
        </ADrawer>
      )}
    </>
  )
}

function Title({ text, style }: { text: string; style?: CSSProperties }) {
  return (
    <div className={styles.title} style={style}>
      <span className={styles.text}>
        {text}
        {/*         <span className={styles.h}>{text.split(' ')[0].slice(0, 1)}</span>
        <span>{text.split(' ')[0].slice(1)}</span>
        <span className={styles.h} style={{ marginLeft: 8 }}>
          {text.split(' ')[1].slice(0, 1)}
        </span>
        <span>{text.split(' ')[1].slice(1)}</span> */}
      </span>
    </div>
  )
}

export default InfoDrawerBtn
